import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { DatePicker } from "../ui/DatePicker";
import { Download, AlertCircle, CheckCircle } from "lucide-react";
import { userService } from "../../services/userService";
import {
  convertToCSV,
  downloadCSV,
  formatStudentForCSV,
  generateTimestampedFilename,
} from "../../utils/csvExport";
import { Checkbox } from "../ui/Checkbox";

interface ExportStudentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialSearchTerm?: string;
  initialVerificationStatus?: "all" | "verified" | "unverified";
}

interface FieldOption {
  key: string;
  label: string;
  defaultSelected: boolean;
}

export function ExportStudentsDialog({
  isOpen,
  onClose,
  initialSearchTerm,
  initialVerificationStatus,
}: ExportStudentsDialogProps) {
  const [filters, setFilters] = useState<{
    verificationStatus: string;
    fromDate: string;
    toDate: string;
  }>({
    verificationStatus: initialVerificationStatus ?? "all",
    fromDate: "",
    toDate: "",
  });
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm ?? "");

  const [recordCount, setRecordCount] = useState<number | null>(null);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const fieldOptions: FieldOption[] = [
    { key: "fullName", label: "Full Name", defaultSelected: true },
    { key: "username", label: "Username", defaultSelected: true },
    { key: "email", label: "Email Address", defaultSelected: true },
    { key: "phoneNumber", label: "Phone Number", defaultSelected: true },
    { key: "role", label: "Account Role", defaultSelected: true },
    { key: "isEmailVerified", label: "Verification Status", defaultSelected: true },
    { key: "createdAt", label: "Date Joined", defaultSelected: true },
    { key: "updatedAt", label: "Last Updated", defaultSelected: false },
    { key: "isSuperAdmin", label: "Super Admin", defaultSelected: false },
    { key: "permissionsCount", label: "Permissions Count", defaultSelected: false },
  ];

  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(fieldOptions.filter((f) => f.defaultSelected).map((f) => f.key))
  );

  const buildFilterParams = () => {
    const params: any = { role: "student" };

    if (filters.verificationStatus !== "all") {
      params.isEmailVerified = filters.verificationStatus === "verified";
    }

    if (filters.fromDate) {
      params.fromDate = filters.fromDate;
    }

    if (filters.toDate) {
      params.toDate = filters.toDate;
    }

    if (searchTerm) {
      params.search = searchTerm;
    }

    return params;
  };

  useEffect(() => {
    if (isOpen) {
      fetchRecordCount();
    }
  }, [filters, searchTerm, isOpen]);

  const fetchRecordCount = async () => {
    setIsLoadingCount(true);
    try {
      const params = buildFilterParams();
      const response = await userService.getUserCount(params);
      setRecordCount(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch record count:", error);
      setRecordCount(0);
    } finally {
      setIsLoadingCount(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    setNotification(null);

    try {
      const params = buildFilterParams();
      const response = await userService.exportUsers(params);
      const users = response.users || [];

      if (users.length === 0) {
        setNotification({
          type: "error",
          message: "No records found matching the selected filters.",
        });
        setIsExporting(false);
        return;
      }

      const formattedData = users.map(formatStudentForCSV);

      const selectedFieldLabels = fieldOptions
        .filter((f) => selectedFields.has(f.key))
        .map((f) => ({ key: f.key, label: f.label }));

      const csvContent = convertToCSV(formattedData, selectedFieldLabels);

      const filename = generateTimestampedFilename("students");
      downloadCSV(csvContent, filename);

      setNotification({
        type: "success",
        message: `Successfully exported ${users.length} student(s) to ${filename}`,
      });

      setTimeout(() => {
        onClose();
        setNotification(null);
      }, 2000);
    } catch (error: any) {
      console.error("Export failed:", error);
      setNotification({
        type: "error",
        message:
          error.message || "Failed to export students. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    setFilters({
      verificationStatus: "all",
      fromDate: "",
      toDate: "",
    });
    setSearchTerm("");
    setSelectedFields(
      new Set(fieldOptions.filter((f) => f.defaultSelected).map((f) => f.key))
    );
  };

  const toggleField = (key: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectAllFields = () => {
    setSelectedFields(new Set(fieldOptions.map((f) => f.key)));
  };

  const deselectAllFields = () => {
    setSelectedFields(new Set());
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Students to CSV">
      <div className="space-y-6 py-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Date Range
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <DatePicker
                value={filters.fromDate}
                onChange={(val) => setFilters({ ...filters, fromDate: val })}
                placeholder="From date"
                max={filters.toDate || undefined}
              />
            </div>
            <span className="text-gray-400">to</span>
            <div className="flex-1">
              <DatePicker
                value={filters.toDate}
                onChange={(val) => setFilters({ ...filters, toDate: val })}
                placeholder="To date"
                min={filters.fromDate || undefined}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Search (optional)
          </label>
          <Input
            type="text"
            placeholder="Search by name, username, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 text-sm"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Verification Status
          </label>
          <div className="flex gap-2">
            {[
              { id: "all", label: "All" },
              { id: "verified", label: "Verified" },
              { id: "unverified", label: "Unverified" },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() =>
                  setFilters({ ...filters, verificationStatus: s.id })
                }
                className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-all ${
                  filters.verificationStatus === s.id
                    ? "bg-[#102040] text-white border-[#102040]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Fields to Export
            </label>
            <div className="flex gap-2">
              <button
                onClick={selectAllFields}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={deselectAllFields}
                className="text-xs text-gray-600 hover:text-gray-700"
              >
                Deselect All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {fieldOptions.map((field) => (
              <label
                key={field.key}
                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
              >
                <Checkbox
                  checked={selectedFields.has(field.key)}
                  onChange={() => toggleField(field.key)}
                  className="cursor-pointer"
                />
                <span className="text-sm text-gray-700">{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Records to Export:
            </span>
            <span className="text-lg font-bold text-[#102040]">
              {isLoadingCount ? (
                <div className="animate-spin inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
              ) : recordCount !== null ? (
                recordCount.toLocaleString()
              ) : (
                "—"
              )}
            </span>
          </div>
        </div>

        {notification && (
          <div
            className={`flex items-center gap-2 p-3 rounded-lg ${
              notification.type === "success"
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {notification.type === "success" ? (
              <CheckCircle size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
            <span className="text-sm">{notification.message}</span>
          </div>
        )}

        <div className="flex justify-between gap-3 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={handleReset}
            className="px-6 text-red-600 hover:bg-red-50 hover:border-red-100"
            disabled={isExporting}
          >
            Reset Filters
          </Button>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="px-6"
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              className="px-8 bg-[#102040] hover:bg-[#081a30] flex items-center gap-2"
              disabled={isExporting || recordCount === 0 || selectedFields.size === 0}
            >
              {isExporting ? (
                <>
                  <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Export CSV
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
